import { unwrap } from '@mrigl/api-client';
import { pickLocalized } from '@mrigl/shared';
import { Alert, Button, Card, SelectField, TextAreaField, TextField } from '@mrigl/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../api.js';
import { Loading } from '../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  SortHeader,
  useGridState,
  usePagedData,
  type Paged,
} from '../grid.js';
import { useI18n } from '../i18n.js';

interface Listing {
  storeId: string;
  slug: string;
  storeName: Partial<Record<string, string>>;
  storeStatus: 'provisioning' | 'active' | 'suspended' | 'closed';
  host: string | null;
  productId: string;
  handle: string;
  title: Partial<Record<string, string>>;
  productStatus: 'draft' | 'active' | 'archived';
  matchedTerms: string[];
  updatedAt: string;
}
type Sort = 'updatedAt' | '-updatedAt';

function listingHref(host: string | null, handle: string): string | null {
  if (host === null) return null;
  const local = host.includes('localhost');
  return `${local ? 'http' : 'https'}://${host}${local ? ':3100' : ''}/products/${handle}`;
}

/** Operator inbox: watched words, matching or recent listings, hide or suspend. */
export function OperatorWatchPage() {
  const { t, locale, formatDateTime } = useI18n();
  const [termsText, setTermsText] = useState('');
  const [suggested, setSuggested] = useState<string[]>([]);
  const [termsLoaded, setTermsLoaded] = useState(false);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'hide' | 'suspend'; id: string } | null>(null);
  const grid = useGridState({
    sort: '-updatedAt',
    sortable: ['updatedAt'],
    filters: { view: 'flagged', q: '' },
  });
  const { filters } = grid;
  const key = JSON.stringify([grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error, reload } = usePagedData<Listing>(
    key,
    async (): Promise<Paged<Listing>> => {
      const terms = await unwrap(api.GET('/operator-api/v1/catalog-watch/terms'));
      setSuggested(terms.suggested);
      setTermsLoaded(true);
      setTermsText((current) =>
        current === '' && !termsLoaded ? terms.terms.join('\n') : current,
      );
      return unwrap(
        api.GET('/operator-api/v1/catalog-watch/listings', {
          params: {
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as Sort,
              view: filters.view as 'flagged' | 'recent',
              ...(filters.q.trim() === '' ? {} : { q: filters.q.trim() }),
            },
          },
        }),
      );
    },
    grid.setPage,
  );

  async function saveTerms() {
    setActionError(null);
    setNotice(null);
    setBusy('terms');
    try {
      const terms = termsText
        .split(/\r?\n/u)
        .map((line) => line.trim().toLowerCase())
        .filter((line) => line.length > 0);
      await unwrap(api.PUT('/operator-api/v1/catalog-watch/terms', { body: { terms } }));
      setNotice(t('admin.operator.watch.termsSaved'));
      reload();
    } catch (failure) {
      setActionError(errorMessage(t, failure));
    } finally {
      setBusy(null);
    }
  }

  async function hide(item: Listing) {
    setActionError(null);
    setNotice(null);
    setBusy(item.productId);
    try {
      await unwrap(
        api.POST('/operator-api/v1/catalog-watch/listings/{storeId}/{productId}/hide', {
          params: { path: { storeId: item.storeId, productId: item.productId } },
          body: { reason: reason.trim() },
        }),
      );
      setNotice(t('admin.operator.watch.hidden'));
      setConfirm(null);
      reload();
    } catch (failure) {
      setActionError(errorMessage(t, failure));
    } finally {
      setBusy(null);
    }
  }

  async function setStore(item: Listing, action: 'suspend' | 'resume') {
    setActionError(null);
    setNotice(null);
    setBusy(item.storeId);
    try {
      await unwrap(
        api.POST(`/operator-api/v1/catalog-watch/stores/{storeId}/${action}`, {
          params: { path: { storeId: item.storeId } },
          body: { reason: reason.trim() },
        }),
      );
      setNotice(
        t(action === 'suspend' ? 'admin.operator.watch.suspended' : 'admin.operator.watch.resumed'),
      );
      setConfirm(null);
      reload();
    } catch (failure) {
      setActionError(errorMessage(t, failure));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mrigl-stack">
      <div className="admin-pagehead">
        <h1 className="mrigl-heading">{t('admin.operator.watch.title')}</h1>
      </div>
      <p className="mrigl-muted">{t('admin.operator.watch.intro')}</p>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null && actionError === null ? null : (
        <Alert tone="error">{actionError ?? error}</Alert>
      )}
      <Card labelledBy="watch-terms">
        <h2 id="watch-terms" className="mrigl-subheading">
          {t('admin.operator.watch.terms')}
        </h2>
        <TextAreaField
          label={t('admin.operator.watch.terms')}
          hint={t('admin.operator.watch.termsHint')}
          value={termsText}
          rows={8}
          onChange={(event) => {
            setTermsText(event.target.value);
          }}
        />
        <div className="admin-form-actions">
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              const extra = suggested.filter(
                (term) =>
                  !termsText
                    .split(/\r?\n/u)
                    .map((line) => line.trim().toLowerCase())
                    .includes(term),
              );
              setTermsText(
                [
                  ...termsText
                    .split(/\r?\n/u)
                    .map((line) => line.trim())
                    .filter(Boolean),
                  ...extra,
                ].join('\n'),
              );
            }}
          >
            {t('admin.operator.watch.suggest')}
          </Button>
          <Button type="button" busy={busy === 'terms'} onClick={() => void saveTerms()}>
            {t('admin.operator.watch.termsSave')}
          </Button>
        </div>
      </Card>
      <GridToolbar activeFilters={grid.activeFilters} onReset={grid.reset}>
        <SelectField
          label={t('admin.operator.watch.flags')}
          value={filters.view}
          onChange={(event) => {
            grid.setFilter('view', event.target.value);
          }}
          options={[
            { value: 'flagged', label: t('admin.operator.watch.flags') },
            { value: 'recent', label: t('admin.operator.watch.recent') },
          ]}
        />
        <TextField
          label={t('admin.operator.watch.search')}
          value={filters.q}
          onChange={(event) => {
            grid.setFilter('q', event.target.value);
          }}
        />
        <TextField
          label={t('admin.operator.watch.reason')}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </GridToolbar>
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={
              filters.view === 'flagged' && termsText.trim() === ''
                ? t('admin.operator.watch.noTerms')
                : t('admin.operator.watch.empty')
            }
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  <SortHeader
                    field="updatedAt"
                    label={t('admin.reviews.column.date')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                  <th scope="col">{t('admin.operator.watch.store')}</th>
                  <th scope="col">{t('admin.operator.watch.product')}</th>
                  <th scope="col">{t('admin.operator.watch.matched')}</th>
                  <th scope="col">{t('admin.product.column.status')}</th>
                  <th scope="col">{t('admin.operator.watch.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => {
                  const name = pickLocalized(item.title, locale, 'fr') ?? item.handle;
                  const store = pickLocalized(item.storeName, locale, 'fr') ?? item.slug;
                  const href = listingHref(item.host, item.handle);
                  const hideKey = `hide:${item.productId}`;
                  const suspendKey = `suspend:${item.storeId}`;
                  return (
                    <tr key={`${item.storeId}:${item.productId}`}>
                      <td className="admin-nowrap">{formatDateTime(item.updatedAt)}</td>
                      <td>
                        <Link to={`/stores/${item.storeId}`} dir="auto">
                          {store}
                        </Link>
                        <span className="mrigl-muted mrigl-ltr admin-feedback-page">
                          {item.slug}
                        </span>
                      </td>
                      <td className="admin-review-cell">
                        {href === null ? (
                          <span dir="auto">{name}</span>
                        ) : (
                          <a href={href} target="_blank" rel="noreferrer" dir="auto">
                            {name}
                          </a>
                        )}
                      </td>
                      <td dir="auto">{item.matchedTerms.join(', ')}</td>
                      <td>{t(`admin.product.status.${item.productStatus}`)}</td>
                      <td>
                        <div className="admin-form-actions">
                          {confirm?.kind === 'hide' && confirm.id === hideKey ? (
                            <Button
                              variant="danger"
                              busy={busy === item.productId}
                              onClick={() => void hide(item)}
                            >
                              {t('admin.operator.watch.confirmHide')}
                            </Button>
                          ) : (
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setConfirm({ kind: 'hide', id: hideKey });
                              }}
                            >
                              {t('admin.operator.watch.hide')}
                            </Button>
                          )}
                          {item.storeStatus === 'suspended' ? (
                            <Button
                              variant="secondary"
                              busy={busy === item.storeId}
                              onClick={() => void setStore(item, 'resume')}
                            >
                              {t('admin.operator.watch.resume')}
                            </Button>
                          ) : confirm?.kind === 'suspend' && confirm.id === suspendKey ? (
                            <Button
                              variant="danger"
                              busy={busy === item.storeId}
                              onClick={() => void setStore(item, 'suspend')}
                            >
                              {t('admin.operator.watch.confirmSuspend')}
                            </Button>
                          ) : item.storeStatus === 'active' ? (
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setConfirm({ kind: 'suspend', id: suspendKey });
                              }}
                            >
                              {t('admin.operator.watch.suspend')}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
    </div>
  );
}
